import * as React from 'react';
import cn from 'classnames';
import { useTheme } from '../../../../theme';
import { convertGenderToCorrectValue } from '../../../../utils';
import { Gender, OptionParameterFragment } from '@websky/graphql';
import { CdnIcon } from '@shared/ui';

interface Props {
	name: string;
	options: OptionParameterFragment[];
	value?: string;
	className?: string;
	onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: () => void;
	error?: string;
	disabled?: boolean;
}

export const PassengerSwitch: React.FunctionComponent<Props> = props => {
	const { PassengerSwitch: theme } = useTheme('Passenger');
	const [selectedOption, setSelectedOption] = React.useState<string>(convertGenderToCorrectValue(props.value ?? ''));

	const formattedOptions = React.useMemo(
		() =>
			props.options.map(option => {
				const genderValue = convertGenderToCorrectValue(option.value);

				return {
					key: genderValue,
					label: option.label,
					icon: genderValue === Gender.Male ? <CdnIcon icon="Info/Male" /> : <CdnIcon icon="Info/Female" />
				};
			}),
		[props.options]
	);

	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedOption(event.target.value);

		props.onChange?.(event.target.value, event);
		props.onBlur?.();
	};

	React.useEffect(() => {
		setSelectedOption(convertGenderToCorrectValue(props.value ?? ''));
	}, [props.value]);

	return (
		<div
			className={cn(theme.container, props.className, {
				[theme.disabled]: props.disabled
			})}
		>
			{formattedOptions.map(option => (
				<label
					key={option.key}
					data-test-id="passenger-gender-switch"
					className={cn(theme.element, {
						[theme.element_active]: selectedOption === option.key,
						[theme.element_error]: props.error
					})}
				>
					<input
						type="radio"
						name={`${props.name}_${option.key}`}
						value={option.key}
						onChange={onChange}
						checked={selectedOption === option.key}
						disabled={props.disabled}
					/>
					{option.icon}
				</label>
			))}

			{props.error && <p className={theme.error}>{props.error}</p>}
		</div>
	);
};
